import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { produce } from 'immer';
import { abortController } from 'store/utils';
import measurementsStore from 'store/measurements';
import { createCatalogApi, editCatalogApi, generateCatalogApi, getCatalogApi, getLastCatalogApi, publishCatalogApi, addCatalogProjectApi, removeCatalogProjectApi, addCatalogUnitApi, removeCatalogUnitApi, getAdminPreviewCatalogApi, } from './api';
const initialState = {
    id: undefined,
    name: '',
    lang: 'en',
    mainCurrency: measurementsStore.getState().priceMeasurement || 'USD',
    additionalCurrency: null,
    areaMeasurementUnit: 'ft2',
    theme: 'dark',
    socialNetworks: [],
    unitViewType: 'table',
    showCustomSlide: false,
    customSlideTitle: '',
    customSlideText: '',
    customSlideImgId: null,
    hideSoldUnits: false,
    hideUnitNumber: false,
    showFullUnitsList: false,
    showAreaSlide: false,
    districts: [],
};
const mutateCatalogForSave = (catalog) => (produce(catalog, (draft) => {
    catalog.districts.forEach((district, i) => {
        district.projects?.forEach((project, j) => {
            if (!draft.districts[i]?.projects?.[j]?.units)
                return;
            draft.districts[i].projects[j].units = project.units.map(({ id }) => id);
        });
    });
}));
const actions = {
    removeDistrict: async ({ districtId }, { setState, getState, signal }) => {
        const editedCatalog = produce(getState(), (catalog) => {
            catalog.districts = catalog.districts.filter((item) => item.id !== districtId);
        });
        setState(editedCatalog);
        await editCatalogApi(mutateCatalogForSave(editedCatalog), signal);
    },
    addProject: async ({ projectId, districtId }, { setState, getState, signal }) => {
        const catalogId = getState().id;
        if (!catalogId)
            return;
        const editedCatalog = produce(getState(), (catalog) => {
            const district = catalog.districts.find((item) => item.id === districtId);
            if (!district) {
                const order = catalog?.districts?.length
                    ? Math.max(...catalog.districts.map((item) => item.order || 0)) + 1
                    : 1;
                catalog.districts.push({
                    id: districtId,
                    order,
                    projects: [{
                            id: projectId,
                            order: 1,
                            units: [],
                        }],
                });
            }
            else {
                const project = district.projects?.find((item) => item.id === projectId);
                if (!project) {
                    const order = Math.max(...district.projects?.map((item) => item.order) || []) + 1;
                    district.projects?.push({
                        id: projectId,
                        order,
                        units: [],
                    });
                }
            }
        });
        setState(editedCatalog);
        await addCatalogProjectApi(catalogId, projectId, signal);
    },
    removeProject: async ({ projectId, districtId }, { setState, getState, signal }) => {
        const catalogId = getState().id;
        if (!catalogId)
            return;
        const editedCatalog = produce(getState(), (catalog) => {
            const district = catalog.districts.find((item) => item.id === districtId);
            if (district) {
                district.projects = district.projects?.filter((project) => project.id !== projectId);
                if (!district.projects?.length)
                    catalog.districts = catalog.districts.filter((item) => item.id !== district.id);
            }
        });
        setState(editedCatalog);
        await removeCatalogProjectApi(catalogId, projectId, signal);
    },
    addUnit: async ({ unitId, projectId, districtId }, { setState, getState, signal }) => {
        const catalogId = getState().id;
        if (!catalogId)
            return;
        const editedCatalog = produce(getState(), (catalog) => {
            const districtsLength = catalog?.districts?.length;
            let catalogDistrict = catalog?.districts?.find((item) => item.id === districtId);
            let catalogProject = catalogDistrict?.projects?.find((item) => item.id === projectId);
            if (districtsLength === undefined)
                catalog.districts = [];
            if (!catalogDistrict) {
                const order = catalog?.districts?.length
                    ? Math.max(...catalog.districts.map((item) => item.order || 0)) + 1
                    : 1;
                const additionDistrict = {
                    geometry: undefined,
                    id: districtId,
                    order,
                    projects: [],
                };
                catalog.districts.push(additionDistrict);
                catalogDistrict = additionDistrict;
            }
            if (catalogDistrict && !catalogProject) {
                const order = catalogDistrict?.projects?.length
                    ? Math.max(...catalogDistrict.projects.map((item) => item.order || 0)) + 1
                    : 1;
                const additionProject = {
                    id: projectId,
                    order,
                    units: [],
                };
                catalogDistrict.projects?.push(additionProject);
                catalogProject = additionProject;
            }
            if (catalogDistrict && catalogProject) {
                catalogProject.units.push({ id: unitId });
            }
        });
        setState(editedCatalog);
        // бэк добавляет проект автоматически при добавлении юнита
        await addCatalogUnitApi(catalogId, unitId, signal);
    },
    removeUnit: async ({ unitId, projectId, districtId }, { setState, getState, signal }) => {
        const catalogId = getState().id;
        if (!catalogId)
            return;
        let shouldRemoveProject = false;
        const editedCatalog = produce(getState(), (catalog) => {
            const districtIndex = catalog.districts.findIndex((item) => item.id === districtId);
            const district = catalog.districts[districtIndex];
            const projectIndex = district?.projects?.findIndex((item) => item.id === projectId);
            const project = district?.projects?.[projectIndex ?? -1];
            if (project) {
                project.units = project.units.filter((unit) => unit?.id !== unitId);
                if (!project.units.length) {
                    shouldRemoveProject = true;
                    district.projects = district.projects?.filter((item) => item.id !== projectId);
                }
                if (!district.projects?.length)
                    catalog.districts = catalog.districts.filter((item) => item.id !== districtId);
            }
        });
        setState(editedCatalog);
        await removeCatalogUnitApi(catalogId, unitId, signal);
        // если юнитов не осталось — удаляем проект из каталога
        if (shouldRemoveProject) {
            await removeCatalogProjectApi(catalogId, projectId, signal);
        }
    },
    sort: async (districts, { setState, getState, signal }) => {
        const editedCatalog = produce(getState(), (catalog) => {
            catalog.districts = districts;
        });
        setState(editedCatalog);
        await editCatalogApi(mutateCatalogForSave(editedCatalog), signal);
    },
    clearCatalog: async (_, { setState, getState, signal }) => {
        const editedCatalog = produce(getState(), (catalog) => {
            catalog.districts = [];
        });
        setState(editedCatalog);
        await editCatalogApi(mutateCatalogForSave(editedCatalog), signal);
    },
    setSettings: async (payload, { setState, getState, signal }) => {
        const editedCatalog = produce(getState(), (catalog) => ({
            ...catalog,
            ...payload,
            districts: catalog.districts,
            links: catalog.links,
        }));
        setState(editedCatalog);
        await editCatalogApi(mutateCatalogForSave(editedCatalog), signal);
    },
    resetSettings: async (_, { setState, getState, signal }) => {
        const editedCatalog = produce(getState(), (catalog) => ({
            ...catalog,
            districts: catalog.districts,
            links: catalog.links,
        }));
        setState(editedCatalog);
        await editCatalogApi(mutateCatalogForSave(editedCatalog), signal);
    },
    dropCatalog: async (_, { setState }) => {
        try {
            const { data } = await createCatalogApi(initialState);
            setState(data);
            return data;
        }
        catch (error) {
            setState(initialState);
            //  @ts-ignore
            throw error.message;
        }
    },
    getCatalog: async ({ id }, { setState }) => {
        try {
            const { data } = await getCatalogApi(id);
            setState(data);
            return data;
        }
        catch (error) {
            setState(initialState);
            //  @ts-ignore
            throw error.message;
        }
    },
    getLastCatalog: async (_, { setState }) => {
        try {
            const { data } = await getLastCatalogApi().catch((err) => {
                if (err.response.status === 404)
                    return createCatalogApi(initialState);
                throw err;
            });
            if (data.links.length) {
                const { data: catalog } = await createCatalogApi(mutateCatalogForSave({ ...data, links: [] }));
                setState(catalog);
                return catalog;
            }
            setState(data);
            return data;
        }
        catch (err) {
            setState(initialState);
            const error = err;
            throw error.response?.data.message;
        }
    },
    createCatalog: async (_, { setState }) => {
        try {
            const { data } = await createCatalogApi(initialState);
            setState(data);
            return data;
        }
        catch (error) {
            //  @ts-ignore
            throw error.message;
        }
    },
    editCatalog: async (catalog, { setState, signal }) => {
        try {
            const { data } = await editCatalogApi(mutateCatalogForSave(catalog), signal);
            setState(data);
            return data;
        }
        catch (error) {
            //  @ts-ignore
            throw error.message;
        }
    },
    publishCatalog: async ({ id }, { setState }) => {
        try {
            const { data } = await publishCatalogApi(id);
            setState({ ...data, id: undefined });
            return data;
        }
        catch (error) {
            //  @ts-ignore
            throw error.message;
        }
    },
    generateCatalog: async ({ id }, { setState, getState }) => {
        try {
            const { data: message } = await generateCatalogApi(id);
            const { data: catalog } = await createCatalogApi(mutateCatalogForSave(getState()));
            setState(catalog);
            return message;
        }
        catch (error) {
            //  @ts-ignore
            throw error.message;
        }
    },
    openAdminPreviewCatalog: async (payload) => {
        try {
            const { data: preview } = await getAdminPreviewCatalogApi(payload);
            return preview;
        }
        catch (err) {
            const error = err;
            throw error.response?.data ?? error;
        }
    },
};
const store = create()(devtools(immer((set, get) => abortController({ ...initialState, ...actions }, set, get, 'Catalog')), {
    name: 'Catalog',
}));
export default store;
